/**
 * ISO 3166-1 alpha-2 country codes excluded from Amplitude Session Replay capture.
 *
 * Sources:
 *   - User-provided B2C exclusions (CONENG-2258 handoff): EG, ET, NG, UZ.
 *   - Sanctioned / restricted regions reused from the existing
 *     COUNTRY_CODE_BLOCKLIST (`src/features/internationalization/models/country-code-blocklist.ts`):
 *     RU, BY, CU, IR, KP, SY, UA. We intentionally re-export those here so this
 *     module is the single source of truth for Session Replay geo gating.
 *
 * TODO(CONENG-2258): extend this list with the additional B2G-heavy /
 *   restricted geographies enumerated in the Confluence page
 *   https://udacity.atlassian.net/wiki/spaces/ENG/pages/413630697/Payment+Method+And+Region+Support
 *   The Atlassian MCP could not retrieve that page during the initial
 *   implementation (org-level OAuth scope blocked the cloudId). A human with
 *   page access should append the codes alphabetically and remove this TODO.
 */
import { COUNTRY_CODE_BLOCKLIST } from '~/features/internationalization/models/country-code-blocklist';

export const SESSION_REPLAY_EXCLUDED_COUNTRIES = [...COUNTRY_CODE_BLOCKLIST, 'EG', 'ET', 'NG', 'UZ'] as const;

export type SessionReplayExcludedCountry = (typeof SESSION_REPLAY_EXCLUDED_COUNTRIES)[number];

/**
 * Cloudflare `CF-IPCountry` / `request.cf.country` values that are not verifiable
 * ISO regions. `XX` = country indeterminate; `T1` = Tor exit node.
 * Session Replay fails closed on these (see `isSessionReplayAllowedForCountry`).
 */
export const SESSION_REPLAY_UNVERIFIED_COUNTRY_CODES = ['XX', 'T1'] as const;

/**
 * Local fraction of sessions sampled for Session Replay capture.
 *
 * **Authoritative source is the Amplitude project's remote
 * `sr_sampling_config.sample_rate`** (Settings → Session Replay → Sampling).
 * The Session Replay SDK fetches that on init and overrides this local value
 * on conflict — verified at runtime during CONENG-2258 QA. Keep this in sync
 * with the remote setting so intent is obvious in code review and so new
 * environments without a remote config behave as expected.
 *
 * Start conservative at 10%. Dial up after initial review of replay quota
 * usage and Datadog RUM regression dashboards (see CONENG-2258 handoff
 * section 7, "Production canary" hold gates).
 */
export const SESSION_REPLAY_SAMPLE_RATE = 0.1;

/**
 * Cookie set by Segment that holds the current `analytics_session_id`. We
 * watch this from a Segment source middleware so Session Replay can be told
 * about session id rotations.
 */
export const SEGMENT_SESSION_ID_COOKIE = 'analytics_session_id';
